import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../components/elements/Card';
import * as Unicons from '@iconscout/react-unicons';
import { useHistory, useLocation } from 'react-router-dom';
import AdminLayout from '../../components/layout/admin';
import Breadcrumbs from '../../components/elements/Breadcrumbs';
import ToolBox from '../../components/elements/ToolBox';
import DataTables from '../../components/elements/DataTables';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showNotification } from '../../utilities/mantineNotification';
import CreateBadge from './CreateBadge';
import {
  deleteMasterBadge,
  editMasterBadge,
  getMasterBadge,
  postMasterBadge
} from '../../services/badge';
import dayjs from 'dayjs';
import id from 'dayjs/locale/id';
import { dateFormat } from '../../utilities/dataFormat';
import TableImage from '../../components/fragments/TableImage';
import ModalDelete from '../../components/fragments/ModalDelete/ModalDelete';
import LabelDate from '../../components/fragments/LabelDate';

const columns = [
  {
    heading: 'Badge Name',
    props: 'badgeName',
    id: 'badgeName',
    sortable: true
  },
  {
    heading: 'Badge Image',
    props: (item) => renderImage(item),
    id: 'badgeImage',
    sortable: false
  },
  {
    heading: 'Start Date',
    props: (item) => renderDate(item, 'startDate'),
    id: 'startDate',
    sortable: true
  },
  {
    heading: 'End Date',
    props: (item) => renderDate(item, 'endDate'),
    id: 'endDate',
    sortable: true
  },
  // {
  //   heading: "Publish Time",
  //   props: "publishTime",
  //   id: "publishTime",
  //   sortable: true,
  // },
  {
    heading: 'Action',
    props: 'action',
    sortable: false
  }
];

const renderImage = (item) => {
  return <TableImage imageUrl={item.badgeImage} />;
};

const renderDate = (item, key) => <LabelDate date={item[key]} />;

const MasterBadgePage = (props) => {
  const history = useHistory();
  const location = useLocation();

  const queryParams: any = new URLSearchParams(location.search);

  const paramsObject = {
    search: queryParams.get('search'),
    page: queryParams.get('page'),
    size: queryParams.get('size')
  };

  const [modalConfig, setModalConfig] = useState({
    show: false,
    data: null,
    type: ''
  });

  const [filter, setFilter] = useState({
    search: '',
    page: 1,
    size: 10
  });

  // Queries
  const fetchMasterBadge = useQuery({
    queryKey: ['getMasterBadge', filter],
    queryFn: () =>
      getMasterBadge({
        search: filter.search,
        page: filter.page,
        size: filter.size
      })
  });

  const createMasterBadge = useMutation({
    mutationFn: postMasterBadge,
    onSuccess: () => {
      fetchMasterBadge.refetch();
      handleCloseModal();
      showNotification({
        type: 'success'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  const updateMasterLevel = useMutation({
    mutationFn: editMasterBadge,
    onSuccess: () => {
      fetchMasterBadge.refetch();
      handleCloseModal();
      showNotification({
        type: 'success',
        title: 'Success Update Master Badge',
        message: 'Updated data will be show in the table'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  const removeMasterBadge = useMutation({
    mutationFn: deleteMasterBadge,
    onSuccess: () => {
      fetchMasterBadge.refetch();
      handleCloseModal();
      showNotification({
        type: 'success',
        title: 'Success Delete Master Badge',
        message: 'Delete Master Badge success'
      });
    },
    onError: (error) => {
      showNotification({
        type: 'error',
        message: JSON.stringify(error)
      });
    }
  });

  useEffect(() => {
    if (filter?.search || filter?.page || filter?.size) {
      fetchMasterBadge.refetch();
    }
  }, [filter]);

  useEffect(() => {
    if (paramsObject?.search || paramsObject?.page || paramsObject?.size) {
      setFilter({
        search: paramsObject?.search || '',
        page: paramsObject?.page || 1,
        size: paramsObject?.size || 10
      });
    }
  }, [paramsObject?.search, paramsObject?.page, paramsObject?.size]);

  const buttonOnClick = () => {
    setModalConfig({
      show: true,
      data: null,
      type: 'add'
    });
  };

  const handleSearch = useCallback((data: any) => {
    history.push({
      pathname: '/management/badge',
      search: `?search=${data}&page=1`
    });
  }, []);

  const handleCloseModal = () => {
    setModalConfig({
      show: false,
      data: null,
      type: ''
    });
  };

  const handleDeleteData = (data) => {
    removeMasterBadge.mutate(data);
  };

  const handleSubmitData = (data) => {
    const payload = {
      ...data,
      userIn:
        modalConfig.type === 'edit'
          ? modalConfig.data.userIn
          : props.auth.binusianId,
      userUp: modalConfig.type === 'edit' ? props.auth.binusianId : ''
    };

    if (modalConfig.type === 'edit') {
      updateMasterLevel.mutate({
        ...payload,
        badgeID: modalConfig.data.badgeID
      });
      return;
    }
    createMasterBadge.mutate(payload);
    return;
  };

  return (
    <AdminLayout>
      <Breadcrumbs
        pageTitle="Badge"
        itemList={[
          {
            title: 'Back Office BCL&D',
            href: '#',
            icon: <Unicons.UilFileCopyAlt size={16} />
          },
          {
            title: 'Reward & Gamification',
            href: '#'
          },
          {
            title: 'Badge',
            href: '/management/master-badge'
          }
        ]}
      />
      <Card>
        <ToolBox
          handleSearch={handleSearch}
          buttonOnClick={buttonOnClick}
          buttonText="Create Badge"
        />
        <DataTables
          columns={columns}
          loading={fetchMasterBadge?.isLoading}
          data={fetchMasterBadge?.data?.data?.data || []}
          handleDelete={(data) => {
            setModalConfig({
              show: true,
              data: data,
              type: 'delete'
            });
          }}
          handleEdit={(data) => {
            setModalConfig({
              show: true,
              data: data,
              type: 'edit'
            });
          }}
        />
      </Card>

      <CreateBadge
        isVisible={
          modalConfig.show &&
          (modalConfig.type === 'add' || modalConfig.type === 'edit')
        }
        type={modalConfig.type}
        data={{
          ...modalConfig.data,
          status:
            modalConfig.type === 'edit'
              ? modalConfig.data.status === 'Y'
                ? 'Active'
                : 'InActive'
              : ''
        }}
        onClose={handleCloseModal}
        onSubmit={handleSubmitData}
        loading={createMasterBadge?.isLoading}
      />

      <ModalDelete
        title="Master Badge"
        isVisible={modalConfig.show && modalConfig.type === 'delete'}
        loading={removeMasterBadge.isLoading}
        onClose={handleCloseModal}
        onConfirmation={() =>
          handleDeleteData({
            badgeID: modalConfig.data.badgeID,
            userUp: props.auth.binusianId
          })
        }
      />
    </AdminLayout>
  );
};

export default MasterBadgePage;
